import React, { FC } from 'react';
import styled from 'styled-components';
import { Pagination } from '../../pages/application/main/history';
import { ArrowIcon } from 'elements/icons';
import { OpenLine } from './ResultContainer';
import {useStores} from "stores/root";
import {observer} from "mobx-react-lite";
import {splitIntoSentences} from "../../utils";

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    padding: 24px;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const CloseButton = styled.button`
    position: absolute;
    right: 24px;
    top: 24px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    
    &:hover {
        color: #000;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-right: 40px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
`;


const ContentContainer = styled.div`
    display: flex;
    gap: 8px;
    height: 100%;
    min-height: 400px;
`;

const TextColumn = styled.div`
    flex: 1;
    background: #fff;
    border: 1px solid #EDF1F9;
    border-radius: 12px;
    padding: 16px;
`;

const SourceColumn = styled(TextColumn)`
    background: #F8F9FB;
`;

const TextContentWrapper = styled.div`
    max-height: calc(100% - 16px);
    overflow-y: scroll;
`;

const TextContent = styled.span`
    font-size: 14px;
    line-height: 1.5;
    color: #333;
    margin-bottom: 16px;
    font-family: Satoshi-Regular!important;
    color: #080C1E;
    display: inline;
`;

const HighlightedText = styled(TextContent)`
    background-color: #F2DDA1;
    display: inline;
`;


const HeaderPercentage = styled.div`
    font-size: 18px;
    color: #080C1E;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border: 1px solid #EDF1F9;
    border-radius: 20px;
`;

const HeaderSource = styled.div`
    font-size: 14px;
    color: #5C5E64;
    font-family: Satoshi-Regular!important;
`;

const HeaderText = styled.div`
    font-size: 16px;
    color: #080C1E;
    font-family: Satoshi-Bold!important;
    margin-bottom: 4px;
`;

const SubBlockScore = styled.div`
    font-size: 14px;
    color: #5C5E64;
    font-family: Satoshi-Regular!important;
    border: 1px solid #EDF1F9;
    background-color: #F8F9FB;
    border-radius: 50px;
    padding: 4px 8px;
    width: fit-content;
    position: absolute;
    top: 96px;
    left: 50%;
    transform: translateX(-50%);
`;

interface PlagiarismModalProps {
    onClose: () => void;
    isOpen: boolean;
}

export const PlagiarismModal: FC<PlagiarismModalProps> = observer(({ onClose, isOpen }) => {
    const {plagiarism: p} = useStores()
    if (!isOpen) return null;

    const uniqueSubBlocks = new Set(p.activeGroup?.scores.map((score: { texts: any[]; }) => score.texts.join('')));

    const uniqueSubBlocksArray = Array.from(uniqueSubBlocks).map(phrase => p.activeGroup?.scores.find((score: { texts: any[]; }) => score.texts.join('') === phrase)!);
    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <CloseButton onClick={onClose}>×</CloseButton>
                <Header>
                    <HeaderPercentage>{p.activeGroup?.score}%</HeaderPercentage>
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                        <Title>{p.activeGroup?.title}</Title>
                        <div style={{display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'}} onClick={() => window.open(p.activeGroup?.link, '_blank')}>
                            <HeaderSource>{p.activeGroup?.shortLink}</HeaderSource>
                            <OpenLine />
                        </div>
                    </div>
                </Header>
                <SubBlockScore>Match: {Math.round(p.activeGroup?.scores[p.activeSubgroup - 1].probability! * 100)}%</SubBlockScore>

                <ContentContainer>
                    <TextColumn>
                        <HeaderText>Your text</HeaderText>
                        <TextContentWrapper>
                            {splitIntoSentences(p.originalText).map((sentence, index) => {
                                const filteredSentence = sentence.trim().toLowerCase().replace(/[^a-zа-я]/g, '');
                                const texts = uniqueSubBlocksArray[p.activeSubgroup - 1].texts;
                                var isMatch = false;
                                for (const text of texts) {
                                    if (filteredSentence === text.trim().toLowerCase().replace(/[^a-zа-я]/g, '')) {
                                        isMatch = true;
                                        break;
                                    }
                                }
                                return (
                                    <React.Fragment key={index}>
                                        {isMatch ? (
                                            <HighlightedText>{sentence}</HighlightedText>
                                        ) : (
                                            <TextContent>{sentence}</TextContent>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TextContentWrapper>
                        
                    </TextColumn>

                    <SourceColumn>
                        <HeaderText>Source</HeaderText>
                        <HighlightedText>
                            {uniqueSubBlocksArray[p.activeSubgroup - 1].sentence.join(' ')}
                        </HighlightedText>
                    </SourceColumn>
                </ContentContainer>
                <div className='pagination-container-plagiarism-modal'>
                    <div style={{width: '250px'}} className='pagination-container-plagiarism-modal-text'>Matches from {p.activeGroup?.link.length! > 20 ? p.activeGroup?.link.slice(0, 20) + '...' : p.activeGroup?.link}</div>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px'}}>
                        <span style={{color: '#848998', cursor: 'pointer', transform: 'rotate(-90deg)'}} onClick={() => {
                            if (p.activeSubgroup > 1) {
                                p.decrementActiveSubgroup();
                            }
                        }}><ArrowIcon /></span>
                        <span style={{color: '#5C5E64', fontFamily: 'Satoshi-Medium'}}>{p.activeSubgroup}/{uniqueSubBlocks.size}</span>
                        <span style={{color: '#848998', cursor: 'pointer', transform: 'rotate(90deg)'}} onClick={() => {
                            if (p.activeSubgroup < uniqueSubBlocks.size) {
                                p.incrementActiveSubgroup();
                            }
                        }}><ArrowIcon /></span>
                    </div>
                    <div style={{width: '250px'}}></div>

                </div>

                <div style={{
                    width: 'calc(100% + 48px)',
                    height: '1px',
                    backgroundColor: '#EDF1F9',
                    transform: 'translateX(-24px)'
                }}></div>
                <div className='pagination-container-plagiarism-modal'>
                    <div style={{width: '150px'}} className='pagination-container-plagiarism-modal-text'>All sources for this text</div>
                    <Pagination
                        total={p.groupedResults.length}
                        pageSize={1}
                        current={p.groupedResults.findIndex(result => result.shortLink === p.activeGroup?.shortLink) + 1}
                        setCurrentPage={(index: number) => {
                            if (index !== -1) {
                                p.setActiveGroup(p.groupedResults[index - 1].shortLink);
                                p.setActiveSubgroup(1);
                            }
                        }}
                        size={32}
                        showControllButtons={false}
                    />
                    <div style={{width: '150px'}}></div>
                </div>

            </ModalContent>
        </ModalOverlay>
    );
});